import { useState, useEffect } from "react";

/**
 * Custom hook to track countdown to market opening time (13:00 UTC / 18:30 IST daily, 15 mins only)
 * @returns {Object} timeRemaining and isMarketOpen state
 */
const useMarketOpenCountdown = () => {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isMarketOpen, setIsMarketOpen] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      
      // Get UTC time directly (13:00 UTC = 18:30 IST)
      const utcHours = now.getUTCHours();
      const utcMinutes = now.getUTCMinutes();
      
      // Target time: 13:00 UTC (18:30 IST)
      const targetHour = 13;
      const targetMinute = 0;
      
      // Market duration: 15 minutes only
      const marketDurationMinutes = 15;
      
      // Calculate if market is currently open
      const marketIsOpen = utcHours === targetHour && 
        utcMinutes >= targetMinute && 
        utcMinutes < targetMinute + marketDurationMinutes;
      setIsMarketOpen(marketIsOpen);
      
      // Calculate time remaining
      let hoursRemaining, minutesRemaining, secondsRemaining;
      
      if (marketIsOpen) {
        // Market is open - calculate time until close
        const closeMinute = targetMinute + marketDurationMinutes;
        minutesRemaining = closeMinute - utcMinutes - 1;
        hoursRemaining = 0;
        secondsRemaining = 60 - now.getSeconds();
        if (secondsRemaining === 60) secondsRemaining = 0;
      } else {
        // Market is closed - calculate time until next market open
        if (utcHours < targetHour || 
            (utcHours === targetHour && utcMinutes < targetMinute)) {
          // Market opens later today
          hoursRemaining = targetHour - utcHours;
          minutesRemaining = targetMinute - utcMinutes;
        } else {
          // Market opens tomorrow
          hoursRemaining = 24 - utcHours + targetHour;
          minutesRemaining = targetMinute - utcMinutes;
        }
        
        // Adjust for negative minutes
        if (minutesRemaining < 0) {
          hoursRemaining--;
          minutesRemaining += 60;
        }
        
        secondsRemaining = 60 - now.getSeconds();
        if (secondsRemaining === 60) secondsRemaining = 0;
        
        // If seconds is 0, we don't need to adjust minutes
        if (secondsRemaining > 0 && secondsRemaining < 60) {
          if (minutesRemaining === 0) {
            hoursRemaining--;
            minutesRemaining = 59;
          } else {
            minutesRemaining--;
          }
        }
      }
      
      setTimeRemaining({
        hours: hoursRemaining,
        minutes: minutesRemaining,
        seconds: secondsRemaining
      });
    };

    // Calculate immediately
    calculateTimeRemaining();
    
    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);
    
    return () => clearInterval(interval);
  }, []);

  return { timeRemaining, isMarketOpen };
};

export default useMarketOpenCountdown;